import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, Menu, X, User, LogOut } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/api";
import CartDrawer from "./CartDrawer";
const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { cartCount, wishlist, cartOpen, openCartDrawer, closeCartDrawer } = useShop();
  const { user, logout } = useAuth();
  const [openMobile, setOpenMobile] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
      setSearchOpen(false);
      setSearch("");
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/85 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <img
              src={LOGO_URL}
              alt="MileForge"
              className="h-14 lg:h-16 w-auto object-contain"
              loading="eager"
            />
          </Link>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-sm tracking-[0.14em] uppercase transition-colors ${
                    isActive ? "text-[#D96A1B]" : "text-white/80 hover:text-white"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Icons */}
          <div className="flex items-center gap-3">
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 text-white/85 hover:text-[#D96A1B] transition-colors"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <Link
              to="/wishlist"
              data-testid="wishlist-link"
              className="p-2 text-white/85 hover:text-[#D96A1B] transition-colors relative"
              aria-label="Wishlist"
            >
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 text-[10px] bg-[#D96A1B] text-white font-display px-1.5 py-0.5">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <button
              data-testid="cart-toggle"
              onClick={openCartDrawer}
              className="p-2 text-white/85 hover:text-[#D96A1B] transition-colors relative"
              aria-label="Cart"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 text-[10px] bg-[#D96A1B] text-white font-display px-1.5 py-0.5">
                  {cartCount}
                </span>
              )}
            </button>
            {/* User Menu */}
            <div className="relative hidden md:block">
              <button
                data-testid="user-menu-toggle"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="p-2 text-white/85 hover:text-[#D96A1B] transition-colors"
                aria-label="Account"
              >
                <User size={20} />
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setUserMenuOpen(false)} />
                  <div data-testid="user-menu-dropdown" className="absolute right-0 top-full mt-2 w-56 bg-[#0E0E0E] border border-white/10 z-40 py-2">
                    {user ? (
                      <>
                        <div className="px-4 py-2 border-b border-white/10">
                          <div className="overline">Signed in as</div>
                          <div className="text-sm font-body text-white/90 truncate">{user.email}</div>
                        </div>
                        {user.role === "admin" ? (
                          <Link to="/admin" onClick={() => setUserMenuOpen(false)} data-testid="menu-admin" className="block px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B]">Admin Panel</Link>
                        ) : (
                          <>
                            <Link to="/account" onClick={() => setUserMenuOpen(false)} data-testid="menu-account" className="block px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B]">My Account</Link>
                            <Link to="/account/orders" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B]">Orders</Link>
                          </>
                        )}
                        <button data-testid="menu-logout" onClick={() => { setUserMenuOpen(false); logout(); navigate("/"); }} className="w-full text-left px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B] flex items-center gap-2 border-t border-white/10">
                          <LogOut size={12} /> Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/signin" onClick={() => setUserMenuOpen(false)} data-testid="menu-signin" className="block px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B]">Sign In</Link>
                        <Link to="/signup" onClick={() => setUserMenuOpen(false)} data-testid="menu-signup" className="block px-4 py-2.5 font-display text-xs tracking-widest uppercase hover:bg-white/5 hover:text-[#D96A1B]">Create Account</Link>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpenMobile(true)}
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={doSearch} className="border-t border-white/10 bg-[#0A0A0A]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-3">
              <Search size={18} className="text-[#D96A1B]" />
              <input
                data-testid="search-input"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search jackets, mounts, lube..."
                className="flex-1 bg-transparent outline-none font-body text-white placeholder:text-white/40"
              />
              <button data-testid="search-submit" className="font-display text-xs tracking-[0.2em] uppercase text-[#D96A1B]">
                Search
              </button>
            </div>
          </form>
        )}
      </header>

      {/* Mobile Menu */}
      {openMobile && (
        <div className="fixed inset-0 z-50 bg-[#0A0A0A]" data-testid="mobile-menu">
          <div className="flex justify-between items-center p-6 border-b border-white/10">
            <img src={LOGO_URL} alt="MileForge" className="h-12 w-auto object-contain" />
            <button data-testid="mobile-menu-close" onClick={() => setOpenMobile(false)} className="text-white"><X /></button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setOpenMobile(false)}
                className="font-display text-2xl py-3 tracking-tight uppercase border-b border-white/5 hover:text-[#D96A1B]"
              >
                {n.label}
              </Link>
            ))}
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setOpenMobile(false)} className="font-display text-sm mt-6 text-[#D96A1B] uppercase tracking-widest">
                {user.role === "admin" ? "Admin Panel" : "My Account"}
              </Link>
            ) : (
              <div className="flex gap-3 mt-6">
                <Link to="/signin" onClick={() => setOpenMobile(false)} className="font-display text-sm text-white/60 uppercase tracking-widest">Sign In</Link>
                <Link to="/signup" onClick={() => setOpenMobile(false)} className="font-display text-sm text-[#D96A1B] uppercase tracking-widest">Sign Up</Link>
              </div>
            )}
          </nav>
        </div>
      )}

      <CartDrawer open={cartOpen} onClose={closeCartDrawer} />
    </>
  );
}
